import React, { useEffect, useState, useCallback } from 'react';
import { Dumbbell, Zap, Snowflake, Star, Clock, Users, CreditCard, Repeat, Package, ArrowRight, Flame } from 'lucide-react';
import axios from 'axios';
import { PublicPointsCalculator, PublicServiceAboCalculator } from '../components/public/PublicShopCalculators';
import ShopCheckoutModal from '../components/public/ShopCheckoutModal';
import GuidedPricing from '../components/public/GuidedPricing';
import { et, etp, EMBED_LANG } from '../i18n/embedLang';
import { applyPublicTenant } from '../utils/publicTenant';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Mandanten-Kontext aus der URL (?tenant=) für alle Requests dieser Seite setzen
applyPublicTenant();

const getCategoryIcon = (cat) => {
  const c = (cat || '').toLowerCase();
  if (c.includes('ems')) return Zap;
  if (c.includes('kryo') || c.includes('cryo')) return Snowflake;
  if (c.includes('flex')) return Flame;
  return Dumbbell;
};

const billingLabel = (cycle) => {
  const map = { monthly: 'monatlich', quarterly: 'vierteljährlich', yearly: 'jährlich', weekly: 'wöchentlich', biweekly: 'zweiwöchentlich', once: 'einmalig' };
  return et(map[cycle] || cycle || 'monatlich');
};

// Website color palette
const C = {
  accent: '#EAB308',
  bg: '#000000',
  text: '#FFFFFF',
  textMuted: '#A3A3A3',
  textSubtle: '#737373',
  cardBg: '#171717',
  border: '#262626',
};

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');`;
const fontFamily = "'Inter', sans-serif";
const headingFont = "'Space Grotesk', sans-serif";

const PublicPricingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  // Multi-Studio (Option B): Studio-Auswahl + persistenter Banner.
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  // Phase B: In-iframe Checkout-Modal
  const [checkoutItem, setCheckoutItem] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const isEmbed = params.get('embed') === 'true';
  const embedBg = params.get('bg') || (isEmbed ? 'transparent' : C.bg);

  // postMessage auto-height for iframe
  // Bug-Report 05/2026: Beim Wechsel auf "Punkt-Pakete" wurden ~549 px
  // gesendet, real waren es 1500–2000 px → Inhalt unten abgeschnitten.
  // Ursache: `document.body.scrollHeight` allein misst nicht zuverlässig
  // wenn der Tab-Panel-Container `display:flex` + dynamische async-Inhalte
  // hat. Fix nach Empfehlung des Bug-Reports:
  //   1. ResizeObserver auf document.documentElement
  //   2. Max(scrollHeight, getBoundingClientRect().height) — der grössere
  //      Wert gewinnt, damit kein Container die Wahrheit beschneiden kann.
  //   3. 50 ms Debounce, damit Layout-Thrashing nicht doppelt feuert.
  //   4. Mehrfach nach Tab-Click/fetch erneut senden.
  const sendHeight = useCallback(() => {
    if (!isEmbed) return;
    try {
      const doc = document.documentElement;
      const body = document.body;
      const height = Math.max(
        doc.scrollHeight,
        doc.offsetHeight,
        body ? body.scrollHeight : 0,
        body ? body.offsetHeight : 0,
        Math.ceil(doc.getBoundingClientRect().height)
      );
      window.parent.postMessage({ type: 'resize', height }, '*');
    } catch (e) { /* same-origin/sandbox */ }
  }, [isEmbed]);

  useEffect(() => {
    if (!isEmbed) return;
    let timer;
    const debounced = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(sendHeight, 50);
    };
    const ro = new ResizeObserver(debounced);
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
    window.addEventListener('load', debounced);
    window.addEventListener('resize', debounced);
    // Initial-Burst: drei zeitversetzte Sends, damit auch der erste Render
    // nach asynchronen Webfont-/Image-Loads noch eine korrekte Höhe meldet.
    const t1 = window.setTimeout(sendHeight, 100);
    const t2 = window.setTimeout(sendHeight, 400);
    const t3 = window.setTimeout(sendHeight, 1000);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', debounced);
      window.removeEventListener('resize', debounced);
      window.clearTimeout(timer);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [isEmbed, sendHeight]);

  useEffect(() => {
    if (isEmbed) {
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.backgroundColor = 'transparent';
      document.body.style.background = 'transparent';
      document.documentElement.style.background = 'transparent';
      document.documentElement.classList.add('embed-mode');
    }
  }, [isEmbed]);

  // Send height on tab change — mehrfach, weil das Punkt-Pakete-Grid
  // asynchron befüllt wird (Sub-Komponente lädt nochmals Pakete vom API).
  useEffect(() => {
    if (!isEmbed) return;
    const t1 = window.setTimeout(sendHeight, 50);
    const t2 = window.setTimeout(sendHeight, 250);
    const t3 = window.setTimeout(sendHeight, 800);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [activeTab, isEmbed, sendHeight]);

  useEffect(() => {
    // Studios laden (Multi-Studio Option B)
    axios.get(`${API}/public/locations`).then(res => {
      const raw = res.data;
      const list = (Array.isArray(raw) ? raw : (raw?.studios || [])).filter(s => s && s.studio_id);
      setStudios(list);
      const studioParam = (params.get('studio') || '').trim().toLowerCase();
      const pre = studioParam
        ? list.find(s => s.slug === studioParam || s.studio_id === studioParam)
        : (list.length === 1 ? list[0] : null);
      if (pre) setSelectedStudio(pre);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pricing-Daten neu laden, sobald das ausgewählte Studio sich ändert
  // (Iter 286: Multi-Studio Service-Filter im Public Pricing).
  useEffect(() => {
    setLoading(true);
    const url = selectedStudio
      ? `${API}/public/pricing?studio_id=${encodeURIComponent(selectedStudio.studio_id)}`
      : `${API}/public/pricing`;
    axios.get(url)
      .then(res => {
        setData(res.data);
        const d = res.data;
        if (d.service_categories?.length > 0) setActiveTab('services');
        else setActiveTab('point_packages');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedStudio]);

  if (loading) {
    return (
      <div style={{ background: isEmbed ? 'transparent' : C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily }}>
        <style>{FONT_IMPORT}{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: `3px solid ${C.accent}`, borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ background: isEmbed ? 'transparent' : C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSubtle, fontFamily }}>
        <style>{FONT_IMPORT}</style>
        {et('Preisliste nicht verfügbar')}
      </div>
    );
  }

  const { service_categories, point_packages, points_packages, credit_packages, recurring_subs: recurring_subs_raw, currency, studio_name, most_popular_service_id } = data;
  // Defensiv: nur Abos mit gültigem numerischem Preis anzeigen (verhindert "CHF NaN")
  const recurring_subs = (recurring_subs_raw || []).filter((s) => Number.isFinite(Number(s?.price)));
  const allPointPkgs = [...(point_packages || []), ...(points_packages || [])];
  // Preislisten-Modus (06/2026): 'guided' = Frage-Dialog statt klassischer Liste
  const pricingMode = data?.pricing_mode || data?.display_settings?.pricing_mode || 'list';
  const secondDiscountPct = Number(data?.second_session_discount_pct ?? data?.display_settings?.second_session_discount_pct) || 25;
  const hasCategories = service_categories?.length > 0;
  const hasPoints = allPointPkgs.length > 0;
  const hasCredits = credit_packages?.length > 0;
  const hasRecurring = recurring_subs?.length > 0;
  // CTA-Ziele für den nicht-eingeloggten Besucher.
  // Embed läuft i. d. R. auf der Studio-Website (nicht auf app.strongfit.ch);
  // der Login muss daher absolute URL zum Portal sein. Wir nehmen den
  // aktuellen Origin als Default — funktioniert sowohl beim direkten Aufruf
  // der Preview als auch beim Iframe-Embed.
  const origin = window.location.origin;
  const loginUrl = `${origin}/portal/login`;
  const bookingUrl = `${origin}/book`;

  const NAV_ITEMS = [
    { key: 'services', label: et('Einzelpreise'), icon: Dumbbell, visible: hasCategories },
    { key: 'point_packages', label: et('Punkt-Pakete'), icon: Star, visible: true },
    { key: 'credit_packages', label: et('Service-Abo'), icon: Package, visible: true },
    { key: 'recurring_subs', label: et('Abos'), icon: Repeat, visible: hasRecurring },
  ].filter(n => n.visible);

  const handleBookingClick = (service) => {
    axios.post(`${API}/public/pricing/click`, { service_id: service.id, service_name: service.name, source: isEmbed ? 'pricing_embed' : 'pricing_page' }).catch(() => {});
    // Multi-Studio: Studio in URL mitgeben, damit /buchen sofort weiß wohin.
    const studioParam = selectedStudio ? `?studio=${selectedStudio.slug}` : '';
    const langParam = EMBED_LANG !== 'de' ? `&lang=${EMBED_LANG}` : '';
    if (isEmbed) {
      // Im iframe: bleib im iframe (kein parent-redirect) → /buchen-Page lädt
      // in der gleichen iframe-Instanz, weil Top-Level URL der Studio-Webseite
      // nicht angefasst wird.
      window.location.href = `/buchen${studioParam}${studioParam ? '&' : '?'}embed=true&service=${encodeURIComponent(service.id)}${langParam}`;
    } else {
      window.location.href = `/buchen${studioParam}${studioParam ? '&' : '?'}service=${encodeURIComponent(service.id)}${langParam}`;
    }
  };

  // Adaptive grid: if only 1-2 cards, make them wider; otherwise 3 columns max.
  // Im Embed-Modus wird die Mindest-Spaltenbreite über die CSS-Var
  // `--embed-grid-min` (Admin-einstellbar) gesteuert. Default 200px.
  const gridCols = (count) => {
    if (isEmbed) {
      if (count <= 2) return `repeat(${count}, minmax(var(--embed-grid-min, 200px), 280px))`;
      return 'repeat(auto-fit, minmax(var(--embed-grid-min, 200px), 1fr))';
    }
    if (count <= 2) return `repeat(${count}, minmax(260px, 380px))`;
    return 'repeat(auto-fit, minmax(260px, 1fr))';
  };

  // Split description into feature-bullet list (newline / ";" / "•").
  // Falls keine Trennzeichen vorhanden, wird die ganze description als ein
  // einzelner Punkt angezeigt.
  const toFeatures = (s) => {
    const desc = (s.description || '').trim();
    let parts = [];
    if (desc) {
      parts = desc
        .split(/\n|;|•|·|\u2022/g)
        .map((p) => p.trim())
        .filter(Boolean);
    }
    // Auto-Features ergänzen, damit die Karte nie leer wirkt
    if (s.duration_minutes > 0) parts.push(etp('{0} Min. pro Session', [s.duration_minutes]));
    if (s.max_participants > 1) parts.push(etp('Bis {0} Teilnehmer:innen', [s.max_participants]));
    return Array.from(new Set(parts)).slice(0, 5);
  };

  const ServiceCard = ({ s, isPopular }) => {
    const features = toFeatures(s);
    return (
      <div
        className={`pricing-card fade-card ${isPopular ? 'is-popular' : ''}`}
        data-testid={`pricing-service-${s.id}`}
      >
        {isPopular && (
          <div className="popular-badge" data-testid="most-popular-badge">{et('BELIEBT')}</div>
        )}

        <h3 className="card-name">{s.name}</h3>

        <div className="price-row">
          <span className="price-currency">{currency}</span>
          <span className={`price-hero ${isPopular ? 'price-hero-gold' : ''}`}>
            {Number(s.price).toFixed(0)}
          </span>
        </div>
        <p className="price-unit-line">
          {s.duration_minutes > 0
            ? etp(s.max_participants > 1 ? 'pro Person ({0} Min)' : 'pro Session ({0} Min)', [s.duration_minutes])
            : s.points_cost > 0
              ? etp('{0} Punkte', [s.points_cost])
              : et('pauschal')}
        </p>

        {features.length > 0 && (
          <ul className="feature-list">
            {features.map((f, i) => (
              <li key={i}>
                <svg viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M2 7.5 L6 11 L12 3.5" />
                </svg>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        <button
          className={`book-btn ${isPopular ? 'book-btn-solid' : ''}`}
          onClick={() => handleBookingClick(s)}
          data-testid={`pricing-book-${s.id}`}
        >
          {et('JETZT BUCHEN')} <ArrowRight />
        </button>
      </div>
    );
  };

  // ---- Iframe-Embed Style: globaler Scale + Per-Element Overrides --
  // Admin kann diese Werte in PortalEmbedSettings einstellen → werden
  // hier als CSS-Variablen angewandt. Defaults entsprechen den
  // bisherigen kompakten Embed-Werten (Iter 287).
  const ES = data?.display_settings?.embed_style || {};
  const scale = typeof ES.scale === 'number' && ES.scale > 0 ? ES.scale : 1;
  const num = (v, fallback) => (typeof v === 'number' && v > 0 ? v : fallback * scale);
  const embedVars = isEmbed
    ? {
        '--embed-card-padding': `${num(ES.card_padding, 16)}px`,
        '--embed-card-radius': `${num(ES.card_radius, 12)}px`,
        '--embed-card-name': `${num(ES.card_name_size, 15)}px`,
        '--embed-price': `${num(ES.price_size, 30)}px`,
        '--embed-price-unit': `${num(ES.price_unit_size, 11)}px`,
        '--embed-feature': `${num(ES.feature_size, 11.5)}px`,
        '--embed-button': `${num(ES.button_size, 10.5)}px`,
        '--embed-button-pad-y': `${num(ES.button_pad_y, 9)}px`,
        '--embed-tab': `${num(ES.tab_size, 11.5)}px`,
        '--embed-cat-heading': `${num(ES.cat_heading_size, 10.5)}px`,
        '--embed-grid-min': `${num(ES.grid_min_width, 200)}px`,
        '--embed-grid-gap': `${num(ES.grid_gap, 12)}px`,
        '--embed-section-margin': `${num(ES.section_margin_bottom, 24)}px`,
      }
    : {};

  return (
    <div data-testid="public-pricing-page" className={isEmbed ? 'is-embed' : ''} style={{ background: embedBg, minHeight: isEmbed ? 'auto' : '100vh', fontFamily, color: C.text, padding: isEmbed ? '4px 0' : '32px 12px', ...embedVars }}>
      <style>{`
        ${FONT_IMPORT}

        /* -------- Hero (Unsere Preise) ------------------------------ */
        .pricing-hero { text-align: center; padding: 24px 0 32px 0; }
        .pricing-hero .accent-line { width: 40px; height: 2px; background: ${C.accent}; margin: 0 auto 18px; border-radius: 2px; box-shadow: 0 0 12px ${C.accent}; }
        .pricing-hero h1 { font-family: ${headingFont}; font-size: 38px; font-weight: 700; color: ${C.text}; letter-spacing: -0.02em; margin: 0; }
        .pricing-hero p  { color: ${C.textMuted}; font-size: 14px; margin: 10px 0 0 0; }

        /* -------- Segmented Tabs ------------------------------------ */
        .pricing-nav { display: inline-flex; gap: 4px; padding: 4px; border-radius: 12px; background: ${C.cardBg}; border: 1px solid ${C.border}; margin: 0 auto 36px auto; }
        .pricing-nav-wrap { display: flex; justify-content: center; }
        .pricing-nav-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 9px; border: none; background: transparent; color: ${C.textMuted}; font-family: ${headingFont}; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; white-space: nowrap; letter-spacing: 0; }
        .pricing-nav-btn:hover { color: ${C.text}; }
        .pricing-nav-btn.active { background: ${C.accent}; color: #000; font-weight: 700; box-shadow: 0 4px 14px rgba(234,179,8,0.25); }
        .pricing-nav-btn svg { width: 14px; height: 14px; }

        /* -------- Category Subheading ------------------------------- */
        .cat-heading { font-family: ${headingFont}; font-size: 12px; font-weight: 600; color: ${C.accent}; text-transform: uppercase; letter-spacing: 0.12em; margin: 0; }
        .section-divider { height: 1px; background: linear-gradient(90deg, ${C.accent}40 0%, transparent 70%); margin: 8px 0 22px 0; }

        /* -------- Pricing Card (StrongFit-Style) -------------------- */
        .pricing-card { position: relative; background: ${C.cardBg}; border: 1px solid ${C.border}; border-radius: 16px; padding: 28px 24px; display: flex; flex-direction: column; transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease; }
        .pricing-card:hover { transform: translateY(-4px); border-color: ${C.accent}40; box-shadow: 0 18px 40px -22px rgba(234,179,8,0.35); }
        .pricing-card.is-popular { border-color: ${C.accent}; box-shadow: 0 18px 48px -20px rgba(234,179,8,0.45); }
        .popular-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: ${C.accent}; color: #000; padding: 4px 14px; border-radius: 6px; font-family: ${headingFont}; font-size: 11px; font-weight: 800; letter-spacing: 0.12em; box-shadow: 0 8px 22px rgba(234,179,8,0.35); }

        .card-name { font-family: ${headingFont}; font-size: 20px; font-weight: 700; color: ${C.text}; margin: 0 0 18px 0; }

        .price-row { display: flex; align-items: flex-start; gap: 6px; line-height: 1; }
        .price-currency { font-family: ${headingFont}; color: ${C.textMuted}; font-size: 12px; font-weight: 500; margin-top: 6px; }
        .price-hero { font-family: ${headingFont}; font-size: 44px; font-weight: 800; color: ${C.text}; letter-spacing: -0.03em; line-height: 1; }
        .price-hero-gold { color: ${C.accent}; }
        .price-unit-line { font-size: 12px; color: ${C.textMuted}; margin: 6px 0 24px 0; }

        .feature-list { list-style: none; padding: 0; margin: 0 0 24px 0; display: flex; flex-direction: column; gap: 12px; }
        .feature-list li { display: flex; align-items: flex-start; gap: 10px; color: ${C.text}; font-size: 13px; line-height: 1.4; }
        .feature-list li svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 3px; stroke: ${C.accent}; stroke-width: 2.2; fill: none; stroke-linecap: round; stroke-linejoin: round; }

        .book-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; margin-top: auto; padding: 13px 16px; border-radius: 10px; border: 1px solid ${C.border}; background: ${C.bg}; color: ${C.text}; font-family: ${headingFont}; font-size: 12px; font-weight: 700; cursor: pointer; transition: all .2s ease; letter-spacing: 0.14em; text-transform: uppercase; }
        .book-btn:hover { border-color: ${C.accent}; color: ${C.accent}; }
        .book-btn svg { width: 14px; height: 14px; }
        .book-btn-solid { background: ${C.accent}; border-color: ${C.accent}; color: #000; }
        .book-btn-solid:hover { background: #FACC15; border-color: #FACC15; color: #000; }

        /* -------- Footer-Note --------------------------------------- */
        .pricing-foot-note { text-align: center; margin-top: 36px; color: ${C.textMuted}; font-size: 13px; }
        .pricing-foot-note strong { color: ${C.accent}; font-weight: 600; }

        /* -------- Misc shared (Punkt/Abo Cards) --------------------- */
        .gold-badge { background: ${C.accent}; color: #000; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
        .meta-row { display: flex; align-items: center; gap: 5px; color: ${C.textMuted}; font-size: 11px; }
        .meta-row svg { width: 11px; height: 11px; color: ${C.accent}; flex-shrink: 0; }
        .price-tag { font-family: ${headingFont}; color: ${C.accent}; font-size: 22px; font-weight: 700; line-height: 1; }
        .price-unit { color: ${C.textSubtle}; font-size: 11px; font-weight: 400; }
        .icon-box { width: 30px; height: 30px; border-radius: 8px; background: rgba(234,179,8,0.1); border: 1px solid ${C.border}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .card-title { font-family: ${headingFont}; font-size: 14px; font-weight: 600; color: ${C.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0; }
        .pkg-description { font-size: 11px; color: ${C.textSubtle}; margin-top: 8px; line-height: 1.45; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .fade-card { animation: fadeUp 0.4s ease-out both; }

        @media (max-width: 640px) {
          .price-hero { font-size: 32px; }
          .pricing-hero h1 { font-size: 30px; }
          .pricing-card { padding: 24px 20px; }
        }

        /* -------- EMBED-MODE: kompaktere Darstellung (Iter 287) -----
           Werte sind als CSS-Variablen definiert, damit der Admin sie
           im Embed-Settings-Panel überschreiben kann (globaler Scale
           und/oder Einzel-Overrides pro Element). */
        .is-embed .pricing-card {
          padding: var(--embed-card-padding, 16px) var(--embed-card-padding, 16px);
          border-radius: var(--embed-card-radius, 12px);
        }
        .is-embed .pricing-card:hover { transform: translateY(-2px); }
        .is-embed .popular-badge { top: -10px; font-size: 9px; padding: 3px 10px; letter-spacing: 0.1em; }
        .is-embed .card-name { font-size: var(--embed-card-name, 15px); margin: 0 0 10px 0; }
        .is-embed .price-currency { font-size: var(--embed-price-unit, 11px); margin-top: 4px; }
        .is-embed .price-hero { font-size: var(--embed-price, 30px); }
        .is-embed .price-unit-line { font-size: var(--embed-price-unit, 11px); margin: 4px 0 14px 0; }
        .is-embed .feature-list { gap: 7px; margin: 0 0 14px 0; }
        .is-embed .feature-list li { font-size: var(--embed-feature, 11.5px); gap: 7px; }
        .is-embed .feature-list li svg { width: 12px; height: 12px; margin-top: 2px; }
        .is-embed .book-btn {
          padding: var(--embed-button-pad-y, 9px) 12px;
          font-size: var(--embed-button, 10.5px);
          letter-spacing: 0.12em; border-radius: 8px;
        }
        .is-embed .book-btn svg { width: 12px; height: 12px; }

        .is-embed .pricing-nav { margin: 0 auto 18px auto; padding: 3px; border-radius: 10px; }
        .is-embed .pricing-nav-btn { padding: 7px 14px; font-size: var(--embed-tab, 11.5px); border-radius: 7px; }
        .is-embed .pricing-nav-btn svg { width: 12px; height: 12px; }

        .is-embed .cat-heading { font-size: var(--embed-cat-heading, 10.5px); letter-spacing: 0.1em; }
        .is-embed .section-divider { margin: 6px 0 14px 0; }

        .is-embed .icon-box { width: 24px; height: 24px; border-radius: 7px; }
        .is-embed .icon-box svg { width: 13px; height: 13px; }
        .is-embed .card-title { font-size: 13px; }
        .is-embed .pkg-description { font-size: 10.5px; margin-top: 5px; }
        .is-embed .price-tag { font-size: calc(var(--embed-price, 30px) * 0.6); }
        .is-embed .price-unit { font-size: 10px; }
        .is-embed .meta-row { font-size: 10px; gap: 4px; }
        .is-embed .meta-row svg { width: 10px; height: 10px; }
        .is-embed .gold-badge { font-size: 9px; padding: 2px 7px; }

        .is-embed .pricing-foot-note { font-size: 11.5px; margin-top: 22px; }
      `}</style>

      <div style={{ maxWidth: isEmbed ? '100%' : 1100, margin: '0 auto', padding: isEmbed ? '0 14px' : 0 }}>
        {!isEmbed && (
          <div className="pricing-hero">
            <div className="accent-line" />
            <h1>{et('Unsere Preise')}</h1>
            <p>{et('Transparente Preise. Keine versteckten Kosten.')}</p>
          </div>
        )}

        {/* Multi-Studio: Studio-Auswahl bei >1 Studio. Pflicht-Schritt bevor
            der User in den Buchungs-Flow geht. Wird ausgeblendet wenn nur
            1 Studio aktiv ist (Single-Studio-Modus = wie früher). */}
        {studios.length > 1 && (
          <div data-testid="pricing-studio-picker" style={{
            background: C.cardBg,
            border: `1px solid ${selectedStudio ? C.border : C.accent + '60'}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              color: selectedStudio ? C.accent : C.text,
              fontFamily: headingFont,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h6"/>
              </svg>
              {selectedStudio ? et('Dein Studio') : et('Wähle dein Studio')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {studios.map(s => {
                const isActive = selectedStudio?.studio_id === s.studio_id;
                return (
                  <button
                    key={s.studio_id}
                    onClick={() => setSelectedStudio(s)}
                    data-testid={`pricing-studio-${s.slug}`}
                    style={{
                      background: isActive ? C.accent : 'transparent',
                      color: isActive ? '#000' : C.text,
                      border: `1px solid ${isActive ? C.accent : C.border}`,
                      borderRadius: 999,
                      padding: '8px 14px',
                      fontFamily: headingFont,
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      cursor: 'pointer',
                      transition: 'all .2s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {s.is_default && <span style={{ fontSize: 11 }}>⭐</span>}
                    {s.short_name || s.name}
                  </button>
                );
              })}
            </div>
            {selectedStudio && (
              <div style={{
                marginTop: 12,
                padding: '8px 12px',
                background: `${C.accent}10`,
                border: `1px solid ${C.accent}30`,
                borderRadius: 8,
                color: C.textMuted,
                fontSize: 12,
                lineHeight: 1.5,
              }}>
                <strong style={{ color: C.accent }}>{et('✓ Ein Kauf — flexibel in jedem Studio nutzbar.')}</strong>{' '}
                {EMBED_LANG === 'de'
                  ? <>Abos & Punkt-Pakete kannst du sowohl in {studios.filter(x => x.studio_id !== selectedStudio.studio_id).map(x => x.short_name || x.name).join(' als auch in ') || 'unseren weiteren Studios'} einlösen.</>
                  : et('Abos & Punkt-Pakete kannst du in allen unseren Studios einlösen.')}
              </div>
            )}
          </div>
        )}

        {/* Geführter Modus (06/2026): Frage-Dialog statt Liste */}
        {pricingMode === 'guided' && (studios.length <= 1 || selectedStudio) && (
          <GuidedPricing
            categories={service_categories || []}
            pointPackages={allPointPkgs}
            creditPackages={credit_packages || []}
            currency={currency}
            discountPct={secondDiscountPct}
            theme={C}
            headingFont={headingFont}
            fontFamily={fontFamily}
            isEmbed={isEmbed}
            basePricePerPoint={Number(data?.base_price_per_point) || 9.375}
            pointsPricingMode={data?.points_pricing_mode || 'bonus'}
            pointTiers={data?.point_tiers || []}
            serviceAboTiers={data?.service_abo_tiers || []}
            onBook={handleBookingClick}
            onBuyPackage={(p) => setCheckoutItem(p.type ? p : { type: 'point_package', id: p.id, name: p.name, price: p.price })}
          />
        )}

        {/* Studio nicht gewählt → Tabs ausblenden mit dezenter Hint */}
        {studios.length > 1 && !selectedStudio ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: C.textMuted,
            fontSize: 14,
          }}>
            {et('↑ Bitte wähle oben dein bevorzugtes Studio, um die passenden Preise zu sehen.')}
          </div>
        ) : pricingMode !== 'guided' && NAV_ITEMS.length > 1 && (
          <div className="pricing-nav-wrap">
            <nav className="pricing-nav" data-testid="pricing-nav">
              {NAV_ITEMS.map(item => (
                <button key={item.key} className={`pricing-nav-btn ${activeTab === item.key ? 'active' : ''}`} onClick={() => setActiveTab(item.key)} data-testid={`pricing-nav-${item.key}`}>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* TAB CONTENT — nur rendern wenn Studio gewählt oder nur 1 Studio */}
        {pricingMode !== 'guided' && (studios.length <= 1 || selectedStudio) && (
        <>
        {/* SERVICES TAB */}
        {activeTab === 'services' && hasCategories && (
          <div>
            {service_categories.map((cat) => {
              const CatIcon = getCategoryIcon(cat.name);
              const count = cat.services.length;
              return (
                <div key={cat.name} style={{ marginBottom: isEmbed ? 'var(--embed-section-margin, 24px)' : 44 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <CatIcon style={{ width: isEmbed ? 13 : 16, height: isEmbed ? 13 : 16, color: C.accent }} />
                    <h2 className="cat-heading">{cat.name}</h2>
                  </div>
                  <div className="section-divider" />
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols(count), gap: isEmbed ? 'var(--embed-grid-gap, 12px)' : 20 }}>
                    {cat.services.map((s) => (
                      <ServiceCard key={s.id} s={s} isPopular={most_popular_service_id && s.id === most_popular_service_id} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* POINT PACKAGES TAB — interaktiver Kalkulator (ersetzt die alten fixen Karten) */}
        {activeTab === 'point_packages' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Star style={{ width: 16, height: 16, color: C.accent }} />
              <h2 className="cat-heading">{et('Punkt-Pakete')}</h2>
            </div>
            <div className="section-divider" />
            <p style={{ fontSize: 11, color: C.textSubtle, marginBottom: 14 }}>
              {et('Punkte können für alle Services eingelöst werden')}
            </p>
            <PublicPointsCalculator
              loginUrl={loginUrl}
              bookingUrl={bookingUrl}
              currency={currency}
              onBuyClick={setCheckoutItem}
            />
            {hasPoints && (
              <>
                <p style={{ fontSize: 11, color: C.textSubtle, marginTop: 20, marginBottom: 8 }}>
                  {et('Oder wähle ein vorgefertigtes Paket:')}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: gridCols(allPointPkgs.length), gap: 12 }}>
                  {allPointPkgs.map((pkg, i) => {
                    const pts = pkg.points || pkg.points_amount || 0;
                    const bonus = pkg.bonus_points || 0;
                    return (
                      <div key={pkg.id} className="pricing-card fade-card" style={{ animationDelay: `${i * 50}ms` }} data-testid={`pricing-points-${pkg.id}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div className="icon-box"><Star style={{ width: 14, height: 14, color: C.accent }} /></div>
                          <p className="card-title">{pkg.name}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                          <span className="price-tag">{currency} {Number(pkg.price).toFixed(0)}</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <div className="meta-row"><CreditCard />{etp('{0} Punkte', [pts])}</div>
                          {bonus > 0 && <span className="gold-badge">{etp('+{0} Bonus', [bonus])}</span>}
                        </div>
                        {pkg.description && <p className="pkg-description">{pkg.description}</p>}
                        <button
                          className="book-btn book-btn-solid"
                          style={{ marginTop: 12 }}
                          onClick={() => setCheckoutItem({ type: 'point_package', id: pkg.id, name: pkg.name, price: pkg.price })}
                          data-testid={`pricing-buy-points-${pkg.id}`}
                        >
                          {et('JETZT KAUFEN')} <ArrowRight />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* CREDIT PACKAGES TAB — Service-Abo Kalkulator */}
        {activeTab === 'credit_packages' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Package style={{ width: 16, height: 16, color: C.accent }} />
              <h2 className="cat-heading">{et('Service-Abo')}</h2>
            </div>
            <div className="section-divider" />
            <p style={{ fontSize: 11, color: C.textSubtle, marginBottom: 14 }}>
              {et('Stelle dir dein Trainingspaket selbst zusammen')}
            </p>
            <PublicServiceAboCalculator
              loginUrl={loginUrl}
              bookingUrl={bookingUrl}
              currency={currency}
              onBuyClick={setCheckoutItem}
            />
            {hasCredits && (
              <>
                <p style={{ fontSize: 11, color: C.textSubtle, marginTop: 20, marginBottom: 8 }}>
                  {et('Oder wähle ein vorgefertigtes 10er-Paket:')}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: gridCols(credit_packages.length), gap: 12 }}>
                  {credit_packages.map((sub, i) => (
                    <div key={sub.id} className="pricing-card fade-card" style={{ animationDelay: `${i * 50}ms` }} data-testid={`pricing-credit-${sub.id}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div className="icon-box"><Package style={{ width: 14, height: 14, color: C.accent }} /></div>
                        <p className="card-title">{sub.name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                        <span className="price-tag">{currency} {Number(sub.price).toFixed(0)}</span>
                        <span className="price-unit">/ {billingLabel(sub.billing_cycle)}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {sub.credits_total > 0 && <div className="meta-row"><CreditCard />{etp('{0} Credits', [sub.credits_total])}</div>}
                        {sub.service_name && <div className="meta-row"><Dumbbell />{sub.service_name}</div>}
                      </div>
                      {sub.description && <p className="pkg-description">{sub.description}</p>}
                      <button
                        className="book-btn book-btn-solid"
                        style={{ marginTop: 12 }}
                        onClick={() => setCheckoutItem({ type: 'service_abo', id: sub.id, name: sub.name, price: sub.price })}
                        data-testid={`pricing-buy-credit-${sub.id}`}
                      >
                        {et('JETZT KAUFEN')} <ArrowRight />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* RECURRING SUBS TAB */}
        {activeTab === 'recurring_subs' && hasRecurring && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Repeat style={{ width: 16, height: 16, color: C.accent }} />
              <h2 className="cat-heading">{et('Wiederkehrende Abos')}</h2>
            </div>
            <div className="section-divider" />
            <p style={{ fontSize: 11, color: C.textSubtle, marginBottom: 14 }}>{et('Monatlich mit automatischer Verlängerung')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(recurring_subs.length), gap: 12 }}>
              {recurring_subs.map((sub, i) => (
                <div key={sub.id} className="pricing-card fade-card" style={{ animationDelay: `${i * 50}ms` }} data-testid={`pricing-recurring-${sub.id}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div className="icon-box"><Repeat style={{ width: 14, height: 14, color: C.accent }} /></div>
                    <p className="card-title">{sub.name}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                    <span className="price-tag">{currency} {Number(sub.price).toFixed(0)}</span>
                    <span className="price-unit">/ {billingLabel(sub.billing_cycle)}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(sub.credits_per_month > 0 || sub.credits_total > 0) && (
                      <div className="meta-row"><CreditCard />{sub.credits_per_month > 0 ? etp('{0} Credits/Monat', [sub.credits_per_month]) : etp('{0} Credits', [sub.credits_total])}</div>
                    )}
                    {sub.points_per_period > 0 && <div className="meta-row"><Star />{etp('{0} Punkte/Monat', [sub.points_per_period])}</div>}
                    {sub.service_name && <div className="meta-row"><Dumbbell />{sub.service_name}</div>}
                  </div>
                  {sub.description && <p className="pkg-description">{sub.description}</p>}
                  <button
                    className="book-btn book-btn-solid"
                    style={{ marginTop: 12 }}
                    onClick={() => setCheckoutItem({ type: 'recurring_sub', id: sub.id, name: sub.name, price: sub.price })}
                    data-testid={`pricing-buy-recurring-${sub.id}`}
                  >
                    {et('ABO STARTEN')} <ArrowRight />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}

        {!isEmbed && (
          <div className="pricing-foot-note">
            <p>{et('★ Erste Beratung ist immer')} <strong>{et('kostenlos & unverbindlich')}</strong></p>
            <p style={{ marginTop: 14, fontSize: 11, color: C.textSubtle }}>
              {etp('Alle Preise in {0} inkl. MwSt.', [currency])} | {studio_name}
            </p>
          </div>
        )}
      </div>

      {/* Phase B: In-iframe Checkout Modal */}
      <ShopCheckoutModal
        open={!!checkoutItem}
        onClose={() => setCheckoutItem(null)}
        item={checkoutItem}
        studio={selectedStudio}
        currency={currency}
      />
    </div>
  );
};

export default PublicPricingPage;
